import {
  DestroyRef,
  Directive,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DeviceDto, PartDto, PartMetricDto } from '../../models/device.model';
import {
  extractUavPartSubType,
  matchesDroneConfiguration,
  normalizeDroneConfiguration,
  resolvePartConfiguration as resolveDronePartConfiguration,
} from '../../utils/drone-configuration';
import {
  MaintenanceCounter,
  MaintenanceCounterType,
  MaintenanceCounterWithPercentage,
  PartSubType,
  PartType,
  PartWithCounterDetails,
  ThresholdType,
  ValueType,
} from '../../models/maintenancelogs.model';
import { DeviceService } from '../../services/device.service';
import {
  getLeastRemainingUsage,
  getMaintenanceStatusColor,
} from '../../utils/maintenance-counter';

@Directive()
export abstract class DroneModelBaseComponent implements OnInit {
  wingLeftColor: string = '#FFFFFF';
  wingRightColor: string = '#FFFFFF';
  tailColor: string = '#FFFFFF';
  bodyColor: string = '#FFFFFF';

  @Input() device?: DeviceDto;
  @Output() configurationTypeChanged = new EventEmitter<string>();
  validParts: PartWithCounterDetails[] = [];
  private readonly deviceService = inject(DeviceService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly primaryMetricsCounterKeySuffix = '_FLIGHT_TIME_TOTAL';
  abstract getConfigurationType(): string[];

  get deviceId(): string | undefined {
    return this.device?.deviceId?.trim() || undefined;
  }

  ngOnInit() {
    if (!this.deviceId) {
      return;
    }

    this.deviceService
      .getDeviceParts(this.deviceId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const deviceParts = this.filterPartsForCurrentModel(
            response.parts ?? [],
          );
          const mappedParts = this.mapPartsToCounterDetails(
            deviceParts,
            new Date().toISOString(),
          ).filter((part) => part.partId.partType !== PartType.PAYLOAD);

          if (!mappedParts.length) {
            this.validParts = [];
            console.warn(
              `No mappable parts found for ${this.getConfigurationType()}.`,
            );
            return;
          }

          this.configurationTypeChanged.emit(
            this.resolveConfigurationToEmit(
              undefined,
              this.deriveConfigurationFromParts(deviceParts),
            ),
          );
          this.validParts = mappedParts;
          this.determineMaintenanceColor();
        },
        error: (error) => {
          console.error(
            `Error loading usage data from device parts for ${this.getConfigurationType()}:`,
            error,
          );
          this.validParts = [];
        },
      });
  }

  private resolveConfigurationToEmit(
    preferredConfiguration?: string,
    derivedConfiguration?: string,
  ): string {
    const normalizedPreferred = this.normalizeConfiguration(
      preferredConfiguration,
    );

    if (
      normalizedPreferred &&
      this.matchesModelConfiguration(normalizedPreferred)
    ) {
      return normalizedPreferred;
    }

    return derivedConfiguration || this.getConfigurationType()[0] || '';
  }

  private deriveConfigurationFromParts(parts: PartDto[]): string {
    const partConfiguration = (parts ?? [])
      .map((part) => this.resolvePartConfiguration(part))
      .find((configuration) => configuration.length > 0);

    return this.normalizeConfiguration(partConfiguration);
  }

  private filterPartsForCurrentModel(parts: PartDto[]): PartDto[] {
    return parts.filter((part) => {
      if (!this.hasSupportedConfiguration(part)) {
        return false;
      }

      if (this.isAllModelConfiguration()) {
        return true;
      }

      const configuration = this.resolvePartConfiguration(part);
      return (
        configuration.length > 0 &&
        this.matchesModelConfiguration(configuration)
      );
    });
  }

  // The backend leaves the fuselage UNSPECIFIED, so also check the subtype.
  private hasSupportedConfiguration(part: PartDto): boolean {
    const declaredConfiguration =
      part.details && part.details.type === 'QsUavPart'
        ? part.details.configuration
        : undefined;

    return [declaredConfiguration, this.resolvePartConfiguration(part)].some(
      (configuration) => /vector|scorpion/i.test(configuration ?? ''),
    );
  }

  private isAllModelConfiguration(): boolean {
    return this.getConfigurationType().some((type) => type === 'AllModel');
  }

  private resolvePartConfiguration(part: PartDto): string {
    return resolveDronePartConfiguration(part);
  }

  private normalizeConfiguration(configuration: string | undefined): string {
    return normalizeDroneConfiguration(configuration);
  }

  private matchesModelConfiguration(configuration: string): boolean {
    return matchesDroneConfiguration(
      configuration,
      this.getConfigurationType(),
    );
  }

  private mapPartsToCounterDetails(
    parts: PartDto[],
    updatedAt: string,
  ): PartWithCounterDetails[] {
    return parts
      .map((part) => this.mapPartToCounterDetails(part, updatedAt))
      .filter((part): part is PartWithCounterDetails => part !== null);
  }

  private mapPartToCounterDetails(
    part: PartDto,
    updatedAt: string,
  ): PartWithCounterDetails | null {
    const partType = this.resolvePartType(part);
    if (!partType) {
      return null;
    }

    const partSubType = extractUavPartSubType(part.details);
    const partId = {
      partType,
      partSubType: this.resolvePartSubType(partSubType),
      id: this.resolvePartId(part),
    };

    let counters: MaintenanceCounterWithPercentage[] = [];
    // An empty value is shown as '-': the part has no usage data at all.
    let primaryCounter: MaintenanceCounter = {
      ...this.createEmptyCounter(updatedAt),
      value: '',
    };

    if (part.metrics?.length) {
      counters = this.mapMetricsToCounters(part.metrics, updatedAt);
      const primaryCounterIndex = this.getPrimaryCounterIndex(counters);
      primaryCounter =
        primaryCounterIndex !== undefined
          ? counters.splice(primaryCounterIndex, 1)[0]
          : this.createEmptyCounter(updatedAt);

      this.sortCountersByUsage(counters);
    }

    return {
      type: {
        partId,
        maintenanceLogs: [],
        maintenanceCounter: primaryCounter,
        threshold: {
          typeOfThreshold: primaryCounter.type,
          value: primaryCounter.threshold,
          unit: primaryCounter.unit,
          valueType: primaryCounter.valueType,
        },
      },
      partId,
      counters,
    };
  }

  private getPrimaryCounterIndex(
    metrics: MaintenanceCounterWithPercentage[],
  ): number | undefined {
    for (let i = 0; i < metrics.length; i++) {
      if (metrics[i].type?.endsWith(this.primaryMetricsCounterKeySuffix)) {
        return i;
      }
    }
    return undefined;
  }

  private mapMetricsToCounters(
    metrics: PartMetricDto[],
    updatedAt: string,
  ): MaintenanceCounterWithPercentage[] {
    const normalizedUpdatedAt = updatedAt || new Date().toISOString();
    const counters = metrics
      .map((metric) => {
        const value = this.toFiniteNumber(metric.value);
        const threshold = this.toFiniteNumber(metric.valueThreshold);

        if (value === null) {
          return null;
        }

        const counter: MaintenanceCounterWithPercentage = {
          type: this.toMaintenanceCounterType(metric.type),
          updatedAt: normalizedUpdatedAt,
          value: String(value),
          threshold: threshold ? String(threshold) : '',
          percentage:
            threshold && threshold > 0 ? (value / threshold) * 100 : undefined,
          valueType: this.toValueType(metric.unit),
        };

        const unit = this.toNonEmptyString(metric.unit);
        if (unit) {
          counter.unit = unit;
        }

        return counter;
      })
      .filter(
        (counter): counter is MaintenanceCounterWithPercentage =>
          counter !== null,
      );

    return counters.length > 0
      ? counters
      : [this.createEmptyCounter(normalizedUpdatedAt)];
  }

  private sortCountersByUsage(
    counters: MaintenanceCounterWithPercentage[],
  ): MaintenanceCounterWithPercentage[] {
    return counters.sort((a, b) => {
      const aPercentage = a.percentage ? a.percentage : 0;
      const bPercentage = b.percentage ? b.percentage : 0;
      return bPercentage - aPercentage;
    });
  }

  private createEmptyCounter(updatedAt: string): MaintenanceCounter {
    return {
      type: ThresholdType.TOTAL_TIME,
      updatedAt,
      value: '0',
      threshold: '0',
      unit: ValueType.SECONDS,
      valueType: ValueType.SECONDS,
    };
  }

  private toMaintenanceCounterType(
    metricType: string | undefined,
  ): MaintenanceCounterType {
    const normalizedMetricType = metricType?.trim();
    if (
      !normalizedMetricType ||
      !Object.values(MaintenanceCounterType).includes(
        normalizedMetricType as MaintenanceCounterType,
      )
    ) {
      return MaintenanceCounterType.UNKNOWN;
    }

    return normalizedMetricType as MaintenanceCounterType;
  }

  private toValueType(unit: string | undefined): ValueType {
    const normalizedUnit = unit?.toLowerCase() ?? '';

    if (normalizedUnit.includes('hour')) {
      return ValueType.HOURS;
    }

    if (normalizedUnit.includes('cycle')) {
      return ValueType.CYCLES;
    }

    return ValueType.SECONDS;
  }

  private resolvePartType(part: PartDto): PartType | null {
    const rawPartType = this.toNonEmptyString(part.type)?.toLowerCase();
    if (rawPartType) {
      switch (rawPartType) {
        case 'body':
          return PartType.BODY;
        case 'left_wing':
          return PartType.LEFT_WING;
        case 'right_wing':
          return PartType.RIGHT_WING;
        case 'rear':
          return PartType.REAR;
        case 'payload':
          return PartType.PAYLOAD;
        default:
          break;
      }
    }

    const detailsPartType = this.toNonEmptyString(
      part.details && part.details.type === 'QsUavPart'
        ? part.details.partType
        : undefined,
    )?.toLowerCase();

    if (!detailsPartType) {
      return null;
    }

    if (
      detailsPartType.includes('fuselage') ||
      detailsPartType.includes('body')
    ) {
      return PartType.BODY;
    }

    if (detailsPartType.includes('left')) {
      return PartType.LEFT_WING;
    }

    if (detailsPartType.includes('right')) {
      return PartType.RIGHT_WING;
    }

    if (detailsPartType.includes('rear') || detailsPartType.includes('tail')) {
      return PartType.REAR;
    }

    if (detailsPartType.includes('payload')) {
      return PartType.PAYLOAD;
    }

    return null;
  }

  private resolvePartId(part: PartDto): string {
    const detailsPartId = this.toNonEmptyString(
      part.details && part.details.type === 'QsUavPart'
        ? part.details.partId
        : undefined,
    );

    return detailsPartId ?? this.toNonEmptyString(part.partId) ?? '';
  }

  private resolvePartSubType(rawPartSubType: string): PartSubType {
    const normalizedPartSubType = rawPartSubType.toLowerCase();
    if (!normalizedPartSubType) {
      return PartSubType.All;
    }

    const resolvedPartSubType = normalizeDroneConfiguration(rawPartSubType);
    if (resolvedPartSubType === PartSubType.VECOTORR24) {
      return PartSubType.VECOTORR24;
    }

    if (resolvedPartSubType === PartSubType.VECOTORR25) {
      return PartSubType.VECOTORR25;
    }

    if (resolvedPartSubType === PartSubType.SCORPIONR24) {
      return PartSubType.SCORPIONR24;
    }

    if (resolvedPartSubType === PartSubType.SCORPIONR25) {
      return PartSubType.SCORPIONR25;
    }

    if (normalizedPartSubType.includes('trinity9000')) {
      return PartSubType.TRINITY9000;
    }

    if (normalizedPartSubType.includes('trinity')) {
      return PartSubType.TRINITY;
    }

    if (
      normalizedPartSubType.includes('aipayload') ||
      normalizedPartSubType.includes('ai_payload')
    ) {
      return PartSubType.AIPAYLOAD;
    }

    if (
      normalizedPartSubType.includes('sbp180') ||
      normalizedPartSubType.includes('spb180')
    ) {
      return PartSubType.SBP180;
    }

    if (
      normalizedPartSubType.includes('sbp120') ||
      normalizedPartSubType.includes('spb120')
    ) {
      return PartSubType.SBP120;
    }

    return PartSubType.All;
  }

  private toNonEmptyString(value: unknown): string | undefined {
    if (typeof value !== 'string') {
      return undefined;
    }

    const trimmedValue = value.trim();
    return trimmedValue.length > 0 ? trimmedValue : undefined;
  }

  private toFiniteNumber(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string') {
      const parsedValue = Number(value);
      return Number.isFinite(parsedValue) ? parsedValue : null;
    }

    return null;
  }

  private determineMaintenanceColor(): void {
    if (!this.validParts.length) {
      console.warn(
        `No valid configuration data available for ${this.getConfigurationType()}!`,
      );
      return;
    }

    this.validParts.forEach((part) => {
      switch (part.partId.partType) {
        case PartType.BODY:
          this.bodyColor = this.getMaintenanceColor(part);
          break;
        case PartType.LEFT_WING:
          this.wingLeftColor = this.getMaintenanceColor(part);
          break;
        case PartType.RIGHT_WING:
          this.wingRightColor = this.getMaintenanceColor(part);
          break;
        case PartType.REAR:
          this.tailColor = this.getMaintenanceColor(part);
          break;
      }
    });
  }

  private getMaintenanceColor(part?: PartWithCounterDetails): string {
    const remainingPercentage = getLeastRemainingUsage(part?.counters);
    return getMaintenanceStatusColor(remainingPercentage);
  }
}
